// Track drawings from real geometry: a track's racing line (OpenStreetMap, lat/lng) projected the way web maps are
// (Web Mercator), so an optional Google satellite image of the same center and zoom lines up under it.

export interface TrackShape {
  track: string;
  /** Racing line, in the direction of travel, as [lat, lng]. */
  path: [number, number][];
  pitLane?: [number, number][];
}

export interface TrackGeometry {
  width: number;
  height: number;
  /** The part of the frame to show: the track plus a margin (the image is in the same frame, so it stays aligned). */
  viewBox: string;
  /** Size of one on-screen unit in frame units (the crop's width / the frame's), to keep dots and text a fixed size. */
  unit: number;
  /** SVG path of the racing line (closed), in the direction of travel. */
  d: string;
  /** Middle of the infield, for the gap text. */
  center: { x: number; y: number };
  /** Start / finish (when set for the track): a point on the line and the direction of travel there (radians). */
  startFinish?: { x: number; y: number; angle: number };
  /** A frame point back to [lat, lng]. */
  toLatLng: (x: number, y: number) => [number, number];
  /** The satellite image to put underneath (only with a Maps key), and the transform that turns it with the drawing. */
  image?: string;
  imageTransform?: string;
}

export const MAP_WIDTH = 640;
export const MAP_HEIGHT = 360;
const PAD = 34;

// Web Mercator "world" coordinates at zoom 0 (0-256).
const world = ([lat, lng]: [number, number]) => {
  const s = Math.sin((lat * Math.PI) / 180);
  return {
    x: ((lng + 180) / 360) * 256,
    y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * 256,
  };
};
const fromWorld = (x: number, y: number): [number, number] => [
  (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / 256))) * 180) / Math.PI,
  (x / 256) * 360 - 180,
];

/**
 * The drawing for a track: the zoom is a whole number (what the Maps Static API takes) chosen so the track fills the
 * frame, and the image (when a key is given) has the same center and zoom, so the line sits on the asphalt.
 */
export const trackGeometry = (shape: TrackShape, mapsKey?: string, sf?: [number, number]): TrackGeometry => {
  const pts = shape.path.map(world);
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  // Taller than wide: it's drawn turned a quarter-turn (below), so fit its height across the frame.
  const tallTrack = maxY - minY > 1.15 * (maxX - minX);
  const [spanW, spanH] = tallTrack ? [maxY - minY, maxX - minX] : [maxX - minX, maxY - minY];
  const fit = Math.min((MAP_WIDTH - 2 * PAD) / (spanW || 1e-9), (MAP_HEIGHT - 2 * PAD) / (spanH || 1e-9));
  const zoom = Math.max(10, Math.min(19, Math.floor(Math.log2(fit))));
  const scale = 2 ** zoom;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const px = (p: { x: number; y: number }) => ({
    x: (p.x - cx) * scale + MAP_WIDTH / 2,
    y: (p.y - cy) * scale + MAP_HEIGHT / 2,
  });
  // A track that's taller than it's wide is turned a quarter-turn (clockwise, about the frame's center), so its long
  // side runs across the box; the image gets the same turn, so they stay lined up.
  const raw = pts.map(px);
  const tall = tallTrack;
  const turn = (p: { x: number; y: number }) =>
    tall ? { x: MAP_WIDTH / 2 - (p.y - MAP_HEIGHT / 2), y: MAP_HEIGHT / 2 + (p.x - MAP_WIDTH / 2) } : p;
  const line = raw.map(turn);
  const d = `M ${line.map((p) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' L ')} Z`;
  const center = {
    x: line.reduce((t, p) => t + p.x, 0) / line.length,
    y: line.reduce((t, p) => t + p.y, 0) / line.length,
  };
  // Start / finish: set by hand per track (trackStartFinish.json; there's no reliable way to work it out: it isn't
  // always beside the middle of pit road, e.g. Phoenix's is at the dogleg). The racing-line point nearest it.
  let startFinish: TrackGeometry['startFinish'];
  if (sf) {
    const target = turn(px(world(sf)));
    let k = 0;
    line.forEach((p, i) => {
      if (Math.hypot(p.x - target.x, p.y - target.y) < Math.hypot(line[k].x - target.x, line[k].y - target.y)) k = i;
    });
    const a = line[(k - 1 + line.length) % line.length];
    const b = line[(k + 1) % line.length];
    startFinish = { x: line[k].x, y: line[k].y, angle: Math.atan2(b.y - a.y, b.x - a.x) };
  }
  // Frame point back to [lat, lng] (for placing start / finish by hand).
  const toLatLng = (x: number, y: number): [number, number] => {
    // undo the quarter-turn, then the projection
    const [ux, uy] = tall ? [MAP_WIDTH / 2 + (y - MAP_HEIGHT / 2), MAP_HEIGHT / 2 - (x - MAP_WIDTH / 2)] : [x, y];
    return fromWorld((ux - MAP_WIDTH / 2) / scale + cx, (uy - MAP_HEIGHT / 2) / scale + cy);
  };
  let image: string | undefined;
  if (mapsKey) {
    const [lat, lng] = fromWorld(cx, cy);
    image =
      `https://maps.googleapis.com/maps/api/staticmap?center=${lat.toFixed(6)},${lng.toFixed(6)}&zoom=${zoom}` +
      `&size=${MAP_WIDTH}x${MAP_HEIGHT}&scale=2&maptype=satellite&key=${encodeURIComponent(mapsKey)}`;
  }
  // Crop to the track (the whole-number zoom can leave up to half the frame empty), keeping the frame's shape.
  const lx = line.map((p) => p.x);
  const ly = line.map((p) => p.y);
  let vw = Math.max(...lx) - Math.min(...lx) + 2 * PAD;
  let vh = Math.max(...ly) - Math.min(...ly) + 2 * PAD;
  const aspect = MAP_WIDTH / MAP_HEIGHT;
  if (vw / vh > aspect) vh = vw / aspect;
  else vw = vh * aspect;
  const vx = (Math.max(...lx) + Math.min(...lx)) / 2 - vw / 2;
  const vy = (Math.max(...ly) + Math.min(...ly)) / 2 - vh / 2;
  const viewBox = [vx, vy, vw, vh].map((v) => v.toFixed(1)).join(' ');
  const imageTransform = tall ? `rotate(90 ${MAP_WIDTH / 2} ${MAP_HEIGHT / 2})` : undefined;
  return {
    width: MAP_WIDTH,
    height: MAP_HEIGHT,
    viewBox,
    unit: vw / MAP_WIDTH,
    d,
    center,
    startFinish,
    image,
    imageTransform,
    toLatLng,
  };
};
